<template>
  <v-container>
    <report
      :title="title"
      :filter="filter"
      :team="team"
      :manager="manager"
      :user="user"
      v-on:refreshData="refreshData"
      v-on:rowClick="rowClick"
      :headers="headers"
      :loading="list_loading"
      :items="items"
      :show-table="!filter.questionId || profile.id != filter.userId"
      enable-business-metrics
    >
      <v-card-text class="py-0">
        <loading-spinner v-if="trend_loading"> </loading-spinner>
        <template v-else>
          <trend-chart
            :loading="trend_loading"
            :height="250"
            :y-axes="{ max: 11, min: -0.5 }"
            :yAxesStepSize="2"
            :label="title"
            enable-business-metrics
            :multiple-trend-data="review_trend_data"
          ></trend-chart>
          <v-row v-if="review.overview">
            <report-overall-card
              :label="
                user ? `Others Scored ${user.firstName}` : 'Overall 360 Score'
              "
              :value="review.overview.score.value | one_decimal"
              :color="$coreScores.getColor(review.overview.score.value)"
            >
              <template v-slot:valueExtension>
                <compare-text
                  ignore-margin
                  align-end
                  :old-value="review.overview.score.compare"
                  :new-value="review.overview.score.value"
                  :timeframeId="filter.timeframeId"
                >
                </compare-text>
              </template>
              <div class="caption lighttext--text">
                {{ review.overview.reviews }}
                reviews collected
              </div>
            </report-overall-card>
            <report-overall-card
              v-if="user && review.overview.reviewedScore.value"
              :label="`${user.firstName} Scored Others`"
              :value="review.overview.reviewedScore.value | one_decimal"
              :color="$coreScores.getColor(review.overview.reviewedScore.value)"
            >
              <template v-slot:valueExtension>
                <compare-text
                  ignore-margin
                  align-end
                  :old-value="review.overview.reviewedScore.compare"
                  :new-value="review.overview.reviewedScore.value"
                  :timeframeId="filter.timeframeId"
                >
                </compare-text>
              </template>
              <div class="caption lighttext--text">
                {{ (review.overview.userRecords || { finished: 0 }).finished }}
                reviews collected
              </div>
            </report-overall-card>
            <report-overall-card
              label="Participation"
              :value="$options.filters.no_decimal(completion) + '%'"
              :color="
                $coreScores.getColor($coreScores.reverseFormat(completion))
              "
            >
              <v-progress-linear
                class="rounded-sm my-auto"
                :value="completion"
                background-color="background"
                :color="
                  $coreScores.getColor($coreScores.reverseFormat(completion))
                "
                height="8"
              ></v-progress-linear>
            </report-overall-card>
            <report-overall-card
              label="Review Management"
              v-if="user"
              hide-value
            >
              <v-btn
                color="performance"
                dark
                @click="editReviewers"
                class="mb-2"
              >
                Who Can Review {{ user.firstName }}?
              </v-btn>
              <div>
                <overlap-avatars
                  :users="reviewers"
                  :size="30"
                  :limit="6"
                ></overlap-avatars>
              </div>
            </report-overall-card>
          </v-row>
        </template>
        <v-tabs v-model="tab" v-if="!filter.questionId">
          <v-tab :class="{ 'd-none': filter.managerId || filter.userId }">
            Departments
          </v-tab>
          <v-tab
            :class="{
              'd-none':
                filter.managerId ||
                filter.userId ||
                (review.overview && review.overview.hideManagers)
            }"
          >
            Teams
          </v-tab>
          <v-tab :class="filter.userId ? 'd-none' : ''">
            Members
          </v-tab>
          <v-tab :class="!filter.userId || filter.categoryId ? 'd-none' : ''">
            Categories
          </v-tab>
          <v-tab :class="!filter.userId ? 'd-none' : ''">
            Questions
          </v-tab>
        </v-tabs>
      </v-card-text>
    </report>
    <edit-user-reviewer-dialog
      ref="editReviewerDialog"
    ></edit-user-reviewer-dialog>
  </v-container>
</template>

<script>
import Report from "@/components/reporting/Report";
import { mapGetters } from "vuex";
import TrendChart from "@/components/reporting/TrendChart";
import reports from "@/helpers/mixins/reports";
import ReportOverallCard from "@/components/reporting/ReportOverallCard";
import CompareText from "@/components/reporting/CompareText";
import OverlapAvatars from "@/components/common/OverlapAvatars";
import EditUserReviewerDialog from "@/components/reviews/EditUserReviewerDialog";
export default {
  name: "ReviewReport",
  props: {
    filter: { type: Object },
    team: { type: Object },
    manager: { type: Object },
    user: { type: Object }
  },
  mixins: [reports],
  components: {
    TrendChart,
    ReportOverallCard,
    CompareText,
    OverlapAvatars,
    EditUserReviewerDialog,
    Report
  },
  data: () => ({
    list_loading: false,
    trend_loading: false,
    tab: 0
  }),
  watch: {
    tab(val, oldVal) {
      if (val != oldVal) {
        this.getList();
      }
    },
    filter(val, oldVal) {
      if (!oldVal && val != null) {
        this.update();
      }
    }
  },
  computed: {
    headers() {
      if (this.question) {
        return [
          { text: "Reviewer", value: "reviewer" },
          {
            text: "Feedback",
            value:
              this.question.questionTypeId ==
              this.$enums.questionTypes.OPINION_SCALE
                ? "feedback"
                : "answerText"
          },
          { text: "Score", value: "answer" }
        ];
      }

      let list = [{ text: "Name", value: "name" }];

      if (!this.filter.userId) {
        list.push({
          text: "Participation",
          value: "userRecords.value.completion"
        });
      }

      list.push({ text: "Reviewers", value: "reviewers" });

      if (this.filter.userId) {
        list.push({ text: "Reviews", value: "reviews" });
      }

      list.push({ text: "Score", value: "score.value" });
      if (this.filter.timeframeId != this.$enums.timeframes.ALL_TIME) {
        list.push({
          text: "Trend",
          value: "score.percentChange"
        });
      }
      list.push({ text: "", value: "actions" });
      return list;
    },
    completion() {
      if (
        this.review.overview &&
        this.review.overview.userRecords &&
        this.review.overview.userRecords.value
      ) {
        return this.review.overview.userRecords.value.completion;
      }
      return 0;
    },
    question() {
      return this.questions.find(x => x.id == this.filter.questionId);
    },
    title() {
      switch (this.filter.coreCardTypeId) {
        case this.$enums.coreCardTypes.REVIEW:
          return "360 Review Score";
        case this.$enums.coreCardTypes.IDEAL_TEAM_PLAYER:
          return "Ideal Team Player Score";
      }
      return "";
    },
    items() {
      let data = [];
      switch (this.tab) {
        case 0:
          data = this.review.teams;
          break;
        case 1:
          data = this.review.managers;
          break;
        case 2:
          data = this.review.members;
          break;
        case 3:
          data = this.review.categories;
          break;
        case 4:
          data = this.review.questions;
          break;
      }
      if (this.filter.questionId) {
        data = this.review.reviews;
      }
      return data || [];
    },
    review_trend_data() {
      let colors = this.getRandomColors(this.multiple_trends.length);
      return this.multiple_trends.map((x, i) => ({
        label:
          this.trend_types
            .filter(y => y.id == x.trendId)
            .map(y => this.t2(y.name))[0] || "Overall",
        color: x.trendId
          ? colors[i]
          : this.$vuetify.theme.themes.light.secondary,
        trend: x.items
      }));
    },
    ...mapGetters({
      categories: "settings/review_categories",
      questions: "settings/review_questions",
      review: "reporting/review",
      reviewers: "user/reviewers",
      profile: "profile/profile",
      is_admin: "profile/is_admin",
      trend_types: "reporting/trend_types",
      multiple_trends: "reporting/multiple_trends"
    })
  },
  created() {
    if (this.filter != null) {
      this.update();
    }
  },
  methods: {
    rowClick(item) {
      let link = this.getLink(item);
      if (!link) {
        return;
      }
      this.$router.push({ path: link });
    },
    update() {
      if (this.filter.userId) {
        this.$store.dispatch("settings/getReviewCategories");
        this.$store.dispatch("user/getReviewers", this.filter.userId);
      }
      this.refreshData();
    },
    getList() {
      this.list_loading = true;
      Promise.all([this.$store.dispatch(this.getAction(), this.filter)])
        .catch(error => {
          this.logError(error);
          this.$root.$alert(
            "Error Occurred",
            "An error occured while loading report."
          );
        })
        .finally(() => {
          this.list_loading = false;
        });
    },
    getAction() {
      if (this.filter.questionId && this.profile.id != this.filter.userId) {
        return "reporting/getReviews";
      }
      switch (this.tab) {
        case 0:
          return "reporting/getReviewTeams";
        case 1:
          return "reporting/getReviewManagers";
        case 2:
          return "reporting/getReviewMembers";
        case 3:
          return "reporting/getReviewCategories";
        case 4:
          return "reporting/getReviewQuestions";
      }
      return "";
    },
    refreshData() {
      this.$store.dispatch("reporting/clearReview").then(() => {
        this.list_loading = true;
        this.trend_loading = true;
        Promise.all([
          this.$store.dispatch("reporting/getReviewTrend", this.filter),
          this.$store.dispatch("reporting/getReview", this.filter)
        ])
          .catch(error => {
            this.logError(error);
            this.$root.$alert(
              "Error Occurred",
              "An error occured while loading report."
            );
          })
          .finally(() => {
            this.list_loading = false;
            this.trend_loading = false;
          })
          .then(() => {
            let newTab = this.getInitialTab(this.review.overview);
            if (newTab) {
              this.tab = newTab;
            }
            this.getList();
          });
      });
    },
    editReviewers() {
      this.$refs.editReviewerDialog.showDialog(this.user);
    }
  }
};
</script>
